'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { useUiContent } from '@/components/ui-content-provider';
import { replacePlaceholders } from '@/lib/ui-content/price-ui-content';
import AmazonIcon from '@/public/icons/amazon-icon.svg';
import { AffiliateLinkTracker } from '../affiliate-link-tracker';
import { RetailerFavicon } from '../retailer-favicon';
import { OffersDisclosure, OffersPanel } from './offers-panel';
import { OffersSheet } from './offers-sheet';
import { PickLine } from './pick-line';
import { useOffersMorph } from './use-offers-morph';
import { shouldFirePrebuyForOffer } from '@/lib/prebuy-prompt';
import { offersDisclosureNotes } from './offer-copy';
import {
  EXPANDED_WIDTH,
  MAX_VISIBLE_OTHER_ROWS,
  PANEL_BORDER_H,
  countOfferRows,
  panelHeight,
  type PreparedOffer,
  type PreparedOffers,
} from './offers';

type BuyNowButtonProps = {
  /** Output of `prepareOffers` — the caller has already checked `rows.length >= 2`. */
  prepared: PreparedOffers;
  localeDetails: LocaleDetails;
  productTitle: string;
  index: number;
  categoryId?: string;
  asin?: string;
  /** Wraps an offer's untagged URL in the /go hop (external-link's `outbound`). */
  hrefFor: (offer: PreparedOffer) => string;
  /** The main CTA's accessible name, composed by external-link. */
  ctaAriaLabel?: string;
  /** The buy-intent freeze. Fires on open AND on every row click. */
  onBuyIntent?: () => void;
  /** Lets the caller promote its z-index while the panel is over the card. */
  onOpenChange?: (open: boolean) => void;
  /**
   * An outbound click on the pick line or an offer row. The caller owns the
   * post-click morph; this is how it learns WHICH merchant just opened. Search
   * rows never fire it (see `shouldFirePrebuyForOffer`).
   */
  onOfferClick?: (offer: PreparedOffer) => void;
};

/**
 * The split "Buy Now / N stores" pill.
 *
 * LEFT zone goes straight to the primary offer — that is the money button, and
 * it must never become a menu the reader has to navigate. RIGHT zone opens the
 * offers list: as an in-place morph on desktop (sm+, 840px here) and as a
 * portal bottom sheet below it. ONE `open` state drives both; which surface the
 * reader sees is decided purely by CSS, so there is no viewport state in render
 * and nothing to desync at hydration.
 *
 * THE PILL DOES NOT GO AWAY WHEN THE PANEL OPENS. It widens in place into the
 * recommended line at the foot of the panel — same pixels, same anchor, same
 * href (pick-line.tsx). That is what lets desktop open the menu on HOVER: a
 * button that vanished from under a passive cursor, with an offer row landing
 * where it had been, is one mis-aimed click away from the wrong retailer.
 * Hover is mouse-only and intent-gated; every path that existed before it —
 * the trigger's click, the keyboard, the sheet — is untouched.
 *
 * Surfaces: the morph element carries the PANEL's material (`bg-light
 * dark:bg-dark`) and the pick line carries the CTA's (`bg-ink`) on top of it.
 * `bg-ink` INVERTS with the theme, near-black on white and near-white on navy,
 * so the pill's contents are `text-light dark:text-black` and every tint inside
 * is `white/x dark:black/x` — the mirror of the panel it sits in. That is the
 * one thing to keep in mind when editing anything inside the pill face.
 */
export function BuyNowButton({
  prepared,
  localeDetails,
  productTitle,
  index,
  categoryId,
  asin,
  hrefFor,
  ctaAriaLabel,
  onBuyIntent,
  onOpenChange,
  onOfferClick,
}: BuyNowButtonProps) {
  const ui = useUiContent();
  const [expanded, setExpanded] = useState(false);
  // Sticky: once the reader has opened the panel, its retailer icons stay
  // fetched. Gating on `morph.open` alone would drop the <img> elements on
  // close and re-probe every source on the next open — a monogram flash on a
  // panel the reader has already seen resolved.
  const [everOpened, setEverOpened] = useState(false);
  const morph = useOffersMorph({
    onOpen: onBuyIntent,
    onOpenChange: (open) => {
      if (open) setEverOpened(true);
      onOpenChange?.(open);
    },
  });

  // A reopened panel starts collapsed again: "show all" is a deliberate act,
  // and a panel that remembers it would open at a different height than the
  // one the pill just measured itself against.
  useEffect(() => {
    if (!morph.open) setExpanded(false);
  }, [morph.open]);

  const { rows, primary, oldestObservedAt } = prepared;
  if (!primary) return null;

  // Freeze, collapse the panel/sheet, then tell the parent which merchant
  // just opened (so it can morph OUR tab). Search rows and information links
  // still freeze and close; they just do not seed a kit, because neither is a
  // purchase of this product.
  const handleOfferNavigate = (offer: PreparedOffer) => {
    onBuyIntent?.();
    morph.closePanel();
    if (offer.kind !== 'info' && shouldFirePrebuyForOffer({ kind: offer.kind })) {
      onOfferClick?.(offer);
    }
  };

  // The primary is the pick line, not a row, so the list is everything else.
  // This arithmetic has to agree with OffersPanel's or the box is sized for a
  // different number of rows than it renders.
  const otherCount = rows.filter((offer) => offer !== primary).length;
  const visibleRowCount = expanded ? otherCount : Math.min(otherCount, MAX_VISIBLE_OTHER_ROWS);
  const showExpander = !expanded && otherCount > MAX_VISIBLE_OTHER_ROWS;
  // The CONSTANT part only. The pick line's height and the strip below it are
  // measured at open time and added here in CSS, because neither is knowable
  // from the row count — see panelHeight() and publishColumnMetrics().
  const panelConstantPx = panelHeight(visibleRowCount, showExpander);
  const panelHeightPx = `calc(${panelConstantPx}px + var(--offers-pick-h, 0px) + var(--offers-gap, 0px))`;
  // The panel FACE's own content height: header + rows + expander, without the
  // box's border. The face is laid out once at this height and clipped by the
  // growing box rather than reflowed on every frame — see
  // `.offers-morph-face-panel` in globals.css for what reflowing looked like.
  const faceHeightPx = panelConstantPx - PANEL_BORDER_H;

  // "{n} stores" counts the rows a reader can buy from: an information link is
  // listed in the panel but is not a store (spec 6.4.5, 6.6.1).
  const storeCount = countOfferRows(rows);
  const storesLabel = `${storeCount} ${ui.stores ?? 'stores'}`;
  const storesAriaLabel = replacePlaceholders(
    ui.seeAllOffers ?? 'See all {n} buying options',
    { n: String(storeCount) },
  );
  // What the prices are, and Amazon's price disclaimer (spec 6.1.4) whenever
  // the panel lists an Amazon price, behind the disclosure's info icon.
  const disclosureNotes = offersDisclosureNotes({
    rows,
    primaryDomainKey: primary.domainKey,
    productTitle,
    region: localeDetails.region,
    ui,
  });

  const panelProps = {
    rows,
    primaryDomainKey: primary.domainKey,
    primary,
    oldestObservedAt,
    localeDetails,
    productTitle,
    index,
    categoryId,
    asin,
    hrefFor,
    ctaAriaLabel,
    expanded,
    onExpand: () => setExpanded(true),
    onRowClick: handleOfferNavigate,
    // The panel face ships in the SSR markup of every card; nothing in it
    // fetches until the reader has actually opened it once.
    active: everOpened,
  };

  return (
    <>
      {/* Holder: keeps the pill's exact pixel footprint in flow while the morph
          is hoisted out of it, so PriceInfo underneath never moves.
          BLOCK-level flex, never `inline-flex`. An inline-level holder sits on a
          line box, and while the pill is in flow it lends that line a real
          baseline. Pinning the morph to `absolute` empties the holder, its
          baseline falls back to its bottom margin edge, and the line box grows
          by the font's descender — 5px of the buy dock, appearing the instant
          the panel opened and taking the price and the tabs with it.
          `max-w-full` so a long locale cannot grow the closed pill past the
          buy column (`sm:w-72 md:w-80`). */}
      <div ref={morph.holderRef} className="relative flex w-fit max-w-full">
        <div
          ref={morph.morphRef}
          // Panel state on the element the CSS keys off, so a browser check can
          // assert the morph opened without inferring it from the trigger's
          // aria-expanded (which lives on a different node) or from pixels.
          data-offers-open={morph.open ? 'true' : 'false'}
          // Pill and panel are the same element, so these fire at the outer
          // edge of whichever one is on screen — there is no gap between
          // trigger and menu for the pointer to fall into.
          {...morph.hoverProps}
          className={`offers-morph max-sm:scale-90 bg-light dark:bg-dark ${
            morph.pinned ? 'is-pinned' : ''
          } ${
            morph.open
              ? // The border lives here rather than on the panel face because
                // `.offers-morph` clips at its own radius — a face-level border
                // would be shaved by the clip. box-sizing is border-box, so
                // adding it costs no layout and cannot jog the grow animation.
                'is-open rounded-2xl border border-black/10 shadow-search-bar dark:border-white/15 dark:shadow-search-bar-dark'
              : // Closed: never wider than the buy column (`sm:w-72 md:w-80`).
                // Dropped while open so `--offers-width` can grow past the
                // holder onto the content column.
                'max-w-full rounded-full'
          }`}
          style={{
            // `--offers-width` is the card's content column, measured on the
            // open click (useOffersMorph) so the panel is the same box as the
            // title and description above it — both edges, not just the left.
            // EXPANDED_WIDTH is the fallback for a card that cannot be
            // measured, never a cap.
            width: morph.open
              ? `var(--offers-width, ${EXPANDED_WIDTH}px)`
              : (morph.pinnedSize?.width ?? undefined),
            height: morph.open ? panelHeightPx : (morph.pinnedSize?.height ?? undefined),
            // Published whether open or closed: while closed the face uses it to
            // sit fully above the pill, outside the clip.
            '--offers-face-h': `${faceHeightPx}px`,
          } as CSSProperties}
        >
          <div className="offers-morph-face-panel">
            <OffersPanel
              variant="morph"
              onBack={() => {
                morph.closePanel();
                morph.triggerRef.current?.focus({ preventScroll: true });
              }}
              backButtonRef={morph.backButtonRef}
              {...panelProps}
            />
          </div>
          {/* The pill face IS the pick line. Ink, not cream: this is the one
              control on the card that has to be unmissable, and `bg-ink` is the
              site's own emphatic surface (ExploreSubmit). It inverts with the
              theme — near-black on white, near-white on navy — so everything
              inside it is `text-light dark:text-black` and every tint is
              `white/x dark:black/x`. That is the opposite of the panel around
              it, which is why the marks and the monogram get their own rules. */}
          <div className="offers-morph-face-pill overflow-hidden rounded-full bg-ink text-light dark:text-black">
            <PickLine
              variant="morph"
              offer={primary}
              offerIndex={rows.indexOf(primary)}
              localeDetails={localeDetails}
              productTitle={productTitle}
              index={index}
              categoryId={categoryId}
              asin={asin}
              href={hrefFor(primary)}
              ctaAriaLabel={ctaAriaLabel}
              onClick={() => handleOfferNavigate(primary)}
              active={everOpened}
            />

            <span
              className="offers-pick-divider my-2 w-px shrink-0 self-stretch bg-white/25 dark:bg-black/20"
              aria-hidden
            />

            <button
              ref={morph.triggerRef}
              type="button"
              aria-haspopup="dialog"
              aria-expanded={morph.open}
              aria-label={storesAriaLabel}
              title={storesAriaLabel}
              onClick={morph.openPanel}
              // Out of the TAB ORDER while open — it is collapsed to zero width
              // then, and tabbing onto an invisible control is a dead stop — but
              // deliberately NOT `aria-hidden`. It is still the labelled toggle,
              // it still carries `aria-expanded`, and Escape focuses it
              // programmatically on the way out; `aria-hidden` would take it out
              // of the accessibility tree with focus about to land on it, which
              // Chrome refuses.
              tabIndex={morph.open ? -1 : undefined}
              // Deliberately NOT the resume-history-chip's
              // `onMouseDown={e => e.preventDefault()}`. That chip is preventing
              // the press from pulling focus out of the search composer; here
              // the trigger is exactly where focus should land, because Escape
              // and the panel's Back button both hand focus back to it. It also
              // costs nothing and risks a lot: under Chrome's touch emulation a
              // defaulted-prevented mousedown swallows the compatibility click,
              // i.e. the tap does nothing on a phone. `select-none` covers the
              // only thing preventDefault was buying — a stray text selection.
              // Label brightens, background does not — see the note on the CTA
              // anchor in pick-line.tsx. The colour is animated by
              // `.offers-pick-toggle` in globals.css, not by Tailwind here.
              className={`offers-pick-toggle flex shrink-0 cursor-pointer select-none items-center gap-1 whitespace-nowrap px-3 text-[0.7rem] font-medium leading-none text-light/75 hover:text-light dark:text-black/70 dark:hover:text-black ${
                localeDetails.languageCode === 'en' ? 'sm:gap-1.5 sm:px-4 sm:text-xs' : ''
              }`}
            >
              {storesLabel}
              <svg
                aria-hidden
                viewBox="0 0 24 24"
                className="h-3 w-3 sm:h-3.5 sm:w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>

          {/* The strip the price block left behind. `fill` stretches it to
              `--offers-gap` instead of the constant the sheet uses. */}
          <div className="offers-morph-face-foot">
            <OffersDisclosure
              languageCode={localeDetails.languageCode}
              fill
              notes={disclosureNotes}
            />
          </div>

        </div>
      </div>

      <OffersSheet open={morph.open} onClose={morph.closePanel} {...panelProps} />
    </>
  );
}
